import { FC } from 'react'

import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { getIsLanguageActive } from 'src/Pages/Artist/SpotifySchedulePage/helpers/helpers'
import { useBootstrapSpotifyCampaignBudget } from 'src/Pages/Artist/SpotifySchedulePage/helpers/useBootstrapSpotifyCampaignBudget'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'
import { useSpotifyCampaignMatching } from 'src/Redux/spotifyCampaignMatchingApi'
import {
  ScheduleTrackStepButton,
  StepButtonStatus,
} from 'src/Containers/ui/ScheduleTrackStepButton'
import { MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { ModalType, ScheduleSteps } from 'src/Types/ui'

import { SpotifySettingsBudget } from './SpotifySettingsBudget'

export const getIsSettingsReady = (
  isGenresActive: boolean,
  isFetching: boolean,
  isLanguageActive: boolean,
  isAiUsageActive: boolean,
  budgetRangeError: boolean,
  isPreReleaseReady: boolean,
): boolean => {
  const hasIncompleteStep =
    isGenresActive || isLanguageActive || isAiUsageActive
  return (
    !hasIncompleteStep && !isFetching && !budgetRangeError && isPreReleaseReady
  )
}

const CAMPAIGN_STEPS = [
  'releaseDate',
  'trackFile',
  'genres',
  'languages',
  'aiUsage',
] as const

export type CampaignStep = (typeof CAMPAIGN_STEPS)[number]

const getPreReleaseStepsCompletion = (
  isPreRelease: boolean,
  hasReleaseDate: boolean,
  hasTrackFile: boolean,
): Pick<Record<CampaignStep, boolean>, 'releaseDate' | 'trackFile'> => ({
  releaseDate: !isPreRelease || hasReleaseDate,
  trackFile: !isPreRelease || hasTrackFile,
})

export const getCampaignStepStatuses = (
  completedSteps: Record<CampaignStep, boolean>,
): Record<CampaignStep, StepButtonStatus> => {
  const activeStep = CAMPAIGN_STEPS.find((step) => !completedSteps[step])
  const getStatus = (step: CampaignStep): StepButtonStatus => {
    if (completedSteps[step]) {
      return StepButtonStatus.completed
    }
    return step === activeStep
      ? StepButtonStatus.active
      : StepButtonStatus.pending
  }

  return {
    releaseDate: getStatus('releaseDate'),
    trackFile: getStatus('trackFile'),
    genres: getStatus('genres'),
    languages: getStatus('languages'),
    aiUsage: getStatus('aiUsage'),
  }
}

const Container = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  @media screen and (min-width: 767px) {
    width: 324px;
  }
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`
const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const StepsButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

const StyledSkeleton = styled(PrimarySkeleton)`
  width: 100%;
  height: 500px;
  border-radius: 16px;
  @media screen and (min-width: 767px) {
    width: 324px;
  }
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`

interface CampaignStepListProps {
  handleClick: (type: ModalType) => void
  isPreRelease: boolean
  statuses: Record<CampaignStep, StepButtonStatus>
}

const renderCampaignStepList = ({
  handleClick,
  isPreRelease,
  statuses,
}: CampaignStepListProps): React.ReactNode => (
  <Container>
    <Title>To set up campaign budget complete these steps:</Title>
    <StepsButtonContainer>
      {isPreRelease && (
        <>
          <ScheduleTrackStepButton
            onClick={() => handleClick(ModalType.ReleaseDate)}
            status={statuses.releaseDate}
          >
            add release date
          </ScheduleTrackStepButton>
          <ScheduleTrackStepButton
            onClick={() => handleClick(ModalType.TrackFile)}
            status={statuses.trackFile}
          >
            add track file
          </ScheduleTrackStepButton>
        </>
      )}
      <ScheduleTrackStepButton
        onClick={() => handleClick(ModalType.Genres)}
        status={statuses.genres}
      >
        add target genres
      </ScheduleTrackStepButton>
      <ScheduleTrackStepButton
        onClick={() => handleClick(ModalType.Languages)}
        status={statuses.languages}
      >
        add track language
      </ScheduleTrackStepButton>
      <ScheduleTrackStepButton
        onClick={() => handleClick(ModalType.AiUsage)}
        status={statuses.aiUsage}
      >
        add AI usage info
      </ScheduleTrackStepButton>
    </StepsButtonContainer>
  </Container>
)

export const SpotifyCampaignSteps: FC = () => {
  const { setSearch } = useCustomSearchParams()
  const params = useParams()
  const { campaignDetails, isPreRelease, releaseDate, trackFile } =
    useSpotifyCampaignStatus()

  useBootstrapSpotifyCampaignBudget()

  const genres = campaignDetails?.genres ?? []
  const languages = campaignDetails?.languages ?? []
  const isGenresActive = !genres.length
  const isLanguageActive = getIsLanguageActive(languages)
  const isAiUsageActive = !campaignDetails?.aiUsage
  const { isError: budgetRangeError, isFetching } = useBudgetRange()
  const { maxCuratorsCount } = useSpotifyCampaignMatching()

  const handleClick = (type: ModalType): void => {
    setSearch({ step: ScheduleSteps.SETTINGS, modal: type })
  }

  const preReleaseSteps = getPreReleaseStepsCompletion(
    isPreRelease,
    Boolean(releaseDate),
    Boolean(trackFile),
  )
  const isCampaignValid = Number(campaignDetails?.id) === Number(params.number)
  const isSettingsReady = getIsSettingsReady(
    isGenresActive,
    isFetching,
    isLanguageActive,
    isAiUsageActive,
    budgetRangeError,
    preReleaseSteps.releaseDate && preReleaseSteps.trackFile,
  )
  const showSettings = isSettingsReady && isCampaignValid
  const isMatchingReady = maxCuratorsCount !== null

  if (isFetching || (showSettings && !isMatchingReady)) {
    return <StyledSkeleton />
  }
  if (showSettings) {
    return <SpotifySettingsBudget />
  }

  return renderCampaignStepList({
    handleClick,
    isPreRelease,
    statuses: getCampaignStepStatuses({
      ...preReleaseSteps,
      genres: !isGenresActive,
      languages: !isLanguageActive,
      aiUsage: !isAiUsageActive,
    }),
  })
}
